import {
    Alert,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Descriptions,
    Form,
    Input,
    InputNumber,
    Popconfirm,
    Space,
    Spin,
    Switch,
    Tag,
    Typography,
    message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    accountSeriesText,
    apiErrorKey,
    buildImportRequest,
    buildSettingsPayload,
    type ConnectionTestResult,
    connectionTestMessage,
    type EInvoiceSettings,
    type LegacyImportResult,
    readTokenChange,
    seriesCheckText,
    type SettingsFormValues,
    toSettingsForm,
} from '../../domain/eInvoice';
import useRequest from '../../hooks/useRequest';

const { Paragraph, Text } = Typography;

const DATE_FORMAT = 'YYYY-MM-DD';
/** The backend's limits (`validateSettingsInput`). */
const MAX_DAYS = 60;
const MAX_SENDS_PER_SECOND = 10;
const MAX_USERNAME_LENGTH = 255;
const SERIES_PATTERN = /^[A-Za-z0-9-]{1,20}$/;

interface SettingsFormFields extends Omit<SettingsFormValues, 'startDate'> {
    startDate: Dayjs | null;
    newToken: string;
    removeToken: boolean;
}

function toFormFields(settings: EInvoiceSettings): SettingsFormFields {
    const values = toSettingsForm(settings);

    return {
        ...values,
        startDate: values.startDate ? dayjs(values.startDate) : null,
        newToken: '',
        removeToken: false,
    };
}

function formatTimestamp(value: string | null): string {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';
}

function ImportReport({ report }: Readonly<{ report: LegacyImportResult }>) {
    const { t } = useTranslation();

    return (
        <Descriptions
            size="small"
            bordered
            column={{ xs: 1, md: 2 }}
            style={{ marginTop: 12 }}
            data-testid="e-invoice-import-report"
            title={t(report.dryRun ? 'eInvoice.import.dryRunTitle' : 'eInvoice.import.doneTitle')}
        >
            <Descriptions.Item label={t('eInvoice.import.legacyRows')}>
                {report.legacyTableFound ? report.legacyRows : t('eInvoice.import.noLegacyTable')}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.imported')}>
                {t('eInvoice.import.importedCounts', report.imported)}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.alreadyPresent')}>
                {report.alreadyPresent}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.superseded')}>
                {report.supersededRows}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.unmatched')}>
                {report.unmatched.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.ambiguous')}>
                {report.ambiguous.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.cancelsLeft')}>
                {report.cancelsLeftForWorker.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.proposedStartDate')}>
                {report.proposedStartDate ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('eInvoice.import.startDate')} span={2}>
                {report.startDate ?? '-'} (
                {t(`eInvoice.import.startDateSource.${report.startDateSource}`)})
            </Descriptions.Item>
        </Descriptions>
    );
}

/** The account's invoice series, each with the series and number of its next invoice. */
function AccountSeriesList({ result }: Readonly<{ result: ConnectionTestResult }>) {
    const { t } = useTranslation();

    if (!result.connected) {
        return null;
    }

    if (result.accountInvoiceSeries.length === 0) {
        return (
            <div data-testid="e-invoice-test-account-series">
                {t('eInvoice.settings.testAccountSeriesNone')}
            </div>
        );
    }

    return (
        <div data-testid="e-invoice-test-account-series">
            {t('eInvoice.settings.testAccountSeries')}
            <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.accountInvoiceSeries.map(series => {
                    const line = accountSeriesText(series);

                    return <li key={series.name}>{t(line.key, line.params)}</li>;
                })}
            </ul>
        </div>
    );
}

/**
 * What the real SmartBill account answered: one line per series a send would use, then every
 * invoice series of the account with its next invoice.
 */
function ConnectionTestReport({ result }: Readonly<{ result: ConnectionTestResult }>) {
    const { t } = useTranslation();
    const headline = connectionTestMessage(result);

    return (
        <Alert
            style={{ marginTop: 12 }}
            type={result.ok ? 'success' : 'error'}
            showIcon
            data-testid="e-invoice-test-result"
            message={t(headline.key, headline.params)}
            description={
                <>
                    {result.series.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {result.series.map(check => {
                                const line = seriesCheckText(check);

                                return (
                                    <li
                                        key={`${check.role}-${check.name}`}
                                        data-testid={`e-invoice-test-series-${check.role}`}
                                    >
                                        {t(line.key, line.params)}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <AccountSeriesList result={result} />
                    <Text type="secondary">{t('eInvoice.settings.testLive')}</Text>
                </>
            }
        />
    );
}

/**
 * The administrator's E-Invoice settings (plan §7.2, tab 2): SmartBill account, series, timing,
 * automatic sending, the connection test and the import of the Delphi history. The token is
 * write-only; the central monitoring copy is the vendor's and only its state is shown.
 */
export default function EInvoiceSettingsTab({ onChanged }: Readonly<{ onChanged: () => void }>) {
    const { t } = useTranslation();
    const request = useRequest();
    const [form] = Form.useForm<SettingsFormFields>();
    const [settings, setSettings] = useState<EInvoiceSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [blockers, setBlockers] = useState<string[]>([]);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
    const [importStartDate, setImportStartDate] = useState<Dayjs | null>(null);
    const [importing, setImporting] = useState<'dry' | 'real' | null>(null);
    const [importReport, setImportReport] = useState<LegacyImportResult | null>(null);
    const removeToken = Form.useWatch('removeToken', form);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await request<{ data: EInvoiceSettings }>({
                path: '/e-invoice/settings',
            });
            setSettings(result.data);
            setBlockers(result.data.autoSubmitBlockers);
            form.setFieldsValue(toFormFields(result.data));
        } catch (error) {
            console.error('Loading the e-invoice settings failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.settings.loadFailed')));
        } finally {
            setLoading(false);
        }
    }, [form, request, t]);

    useEffect(() => {
        void load();
    }, [load]);

    const save = async (fields: SettingsFormFields) => {
        setSaving(true);
        try {
            await request({
                path: '/e-invoice/settings',
                method: 'PUT',
                params: buildSettingsPayload(
                    { ...fields, startDate: fields.startDate?.format(DATE_FORMAT) ?? null },
                    readTokenChange(fields.newToken ?? '', fields.removeToken),
                ),
            });
            message.success(t('eInvoice.settings.saved'));
            await load();
            onChanged();
        } catch (error) {
            console.error('Saving the e-invoice settings failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.settings.saveFailed')));
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const result = await request<{ data: ConnectionTestResult }>({
                path: '/e-invoice/settings/test-connection',
                method: 'POST',
            });
            setTestResult(result.data);
        } catch (error) {
            console.error('Testing the e-invoice connection failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.settings.testFailed')));
        } finally {
            setTesting(false);
        }
    };

    const runImport = async (dryRun: boolean) => {
        setImporting(dryRun ? 'dry' : 'real');
        try {
            const result = await request<{ data: LegacyImportResult }>({
                path: '/e-invoice/import-legacy',
                method: 'POST',
                params: buildImportRequest(dryRun, importStartDate?.format(DATE_FORMAT) ?? null),
            });
            setImportReport(result.data);
            if (!dryRun) {
                message.success(t('eInvoice.import.done'));
                await load();
                onChanged();
            }
        } catch (error) {
            console.error('Importing the legacy e-invoice history failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.import.failed')));
        } finally {
            setImporting(null);
        }
    };

    if (loading && settings === null) {
        return <Spin data-testid="e-invoice-settings-loading" />;
    }

    return (
        <div data-testid="e-invoice-settings">
            <Card size="small" title={t('eInvoice.settings.state')} style={{ marginBottom: 16 }}>
                <Descriptions size="small" column={{ xs: 1, md: 2 }}>
                    <Descriptions.Item label={t('eInvoice.settings.sendingMode')}>
                        {settings?.sending.live ? (
                            <Tag color="red">{t('eInvoice.sending.live')}</Tag>
                        ) : (
                            <Tag color="gold" data-testid="e-invoice-settings-simulated">
                                {t('eInvoice.sending.simulated')}
                            </Tag>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('eInvoice.settings.centralCopy')}>
                        {t(
                            settings?.centralCopyEnabled
                                ? 'eInvoice.settings.on'
                                : 'eInvoice.settings.off',
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('eInvoice.settings.legacyImportedAt')}>
                        {formatTimestamp(settings?.legacyImportedAt ?? null)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('eInvoice.settings.updatedAt')}>
                        {formatTimestamp(settings?.updatedAt ?? null)}
                    </Descriptions.Item>
                </Descriptions>
                {blockers.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        data-testid="e-invoice-blockers"
                        message={t('eInvoice.settings.notReady')}
                        description={
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {blockers.map(blocker => (
                                    <li key={blocker}>{t(blocker)}</li>
                                ))}
                            </ul>
                        }
                    />
                )}
            </Card>

            <Card size="small" title={t('eInvoice.settings.account')} style={{ marginBottom: 16 }}>
                <Form<SettingsFormFields>
                    form={form}
                    layout="vertical"
                    onFinish={values => void save(values)}
                    style={{ maxWidth: 640 }}
                >
                    <Form.Item
                        name="smartBillUsername"
                        label={t('eInvoice.settings.username')}
                        rules={[
                            {
                                max: MAX_USERNAME_LENGTH,
                                message: t('eInvoice.errors.invalidUsername'),
                            },
                        ]}
                    >
                        <Input autoComplete="off" data-testid="e-invoice-username" />
                    </Form.Item>
                    <Form.Item
                        label={t('eInvoice.settings.token')}
                        extra={t(
                            settings?.hasToken
                                ? 'eInvoice.settings.tokenSaved'
                                : 'eInvoice.settings.tokenNotSaved',
                        )}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Form.Item name="newToken" noStyle>
                                <Input.Password
                                    autoComplete="new-password"
                                    disabled={removeToken}
                                    placeholder={t('eInvoice.settings.tokenPlaceholder')}
                                    data-testid="e-invoice-token"
                                />
                            </Form.Item>
                            {settings?.hasToken && (
                                <Form.Item name="removeToken" valuePropName="checked" noStyle>
                                    <Checkbox>{t('eInvoice.settings.removeToken')}</Checkbox>
                                </Form.Item>
                            )}
                        </Space>
                    </Form.Item>
                    <Space wrap size="large">
                        <Form.Item
                            name="seriesName"
                            label={t('eInvoice.settings.series')}
                            rules={[
                                {
                                    pattern: SERIES_PATTERN,
                                    message: t('eInvoice.errors.invalidSeries'),
                                },
                            ]}
                        >
                            <Input style={{ width: 160 }} data-testid="e-invoice-series" />
                        </Form.Item>
                        <Form.Item
                            name="receiptMode"
                            label={t('eInvoice.settings.receiptMode')}
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="receiptSeries"
                            label={t('eInvoice.settings.receiptSeries')}
                            rules={[
                                {
                                    pattern: SERIES_PATTERN,
                                    message: t('eInvoice.errors.invalidSeries'),
                                },
                            ]}
                        >
                            <Input style={{ width: 160 }} />
                        </Form.Item>
                    </Space>
                    <Space wrap size="large">
                        <Form.Item
                            name="submitAfterDays"
                            label={t('eInvoice.settings.submitAfterDays')}
                            rules={[{ required: true, message: t('eInvoice.errors.invalidDays') }]}
                        >
                            <InputNumber min={0} max={MAX_DAYS} precision={0} />
                        </Form.Item>
                        <Form.Item
                            name="cancelWindowDays"
                            label={t('eInvoice.settings.cancelWindowDays')}
                            rules={[{ required: true, message: t('eInvoice.errors.invalidDays') }]}
                        >
                            <InputNumber min={0} max={MAX_DAYS} precision={0} />
                        </Form.Item>
                        <Form.Item
                            name="sendsPerSecond"
                            label={t('eInvoice.settings.sendsPerSecond')}
                            tooltip={t('eInvoice.settings.sendsPerSecondHint')}
                            rules={[
                                {
                                    required: true,
                                    message: t('eInvoice.errors.invalidSendsPerSecond'),
                                },
                            ]}
                        >
                            <InputNumber
                                min={1}
                                max={MAX_SENDS_PER_SECOND}
                                precision={0}
                                data-testid="e-invoice-sends-per-second"
                            />
                        </Form.Item>
                        <Form.Item
                            name="startDate"
                            label={t('eInvoice.settings.startDate')}
                            tooltip={t('eInvoice.settings.startDateHint')}
                        >
                            <DatePicker format={DATE_FORMAT} data-testid="e-invoice-start-date" />
                        </Form.Item>
                    </Space>
                    <Form.Item
                        name="autoSubmit"
                        label={t('eInvoice.settings.autoSubmit')}
                        valuePropName="checked"
                        extra={t('eInvoice.settings.autoSubmitHint')}
                    >
                        <Switch data-testid="e-invoice-auto-submit" />
                    </Form.Item>
                    <Space wrap>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={saving}
                            data-testid="e-invoice-settings-save"
                        >
                            {t('common.save')}
                        </Button>
                        <Button
                            loading={testing}
                            onClick={() => void testConnection()}
                            data-testid="e-invoice-test-connection"
                        >
                            {t('eInvoice.settings.testConnection')}
                        </Button>
                    </Space>
                    {testResult && <ConnectionTestReport result={testResult} />}
                </Form>
            </Card>

            <Card size="small" title={t('eInvoice.import.title')}>
                <Paragraph type="secondary">{t('eInvoice.import.description')}</Paragraph>
                <Space wrap>
                    <Text>{t('eInvoice.import.startDateOverride')}</Text>
                    <DatePicker
                        format={DATE_FORMAT}
                        value={importStartDate}
                        onChange={setImportStartDate}
                    />
                    <Button
                        loading={importing === 'dry'}
                        disabled={importing !== null}
                        onClick={() => void runImport(true)}
                        data-testid="e-invoice-import-dry-run"
                    >
                        {t('eInvoice.import.dryRun')}
                    </Button>
                    <Popconfirm
                        title={t('eInvoice.import.confirm')}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        onConfirm={() => void runImport(false)}
                    >
                        <Button
                            type="primary"
                            loading={importing === 'real'}
                            disabled={importing !== null}
                            data-testid="e-invoice-import-run"
                        >
                            {t('eInvoice.import.run')}
                        </Button>
                    </Popconfirm>
                </Space>
                {importReport && <ImportReport report={importReport} />}
            </Card>
        </div>
    );
}
